import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import ReactQuill from "react-quill";
import SignatureCanvas from "react-signature-canvas";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const quillModules = {
 toolbar: [
 [{ 'header': [1, 2, false] }],
 ['bold', 'italic', 'underline', 'strike', 'blockquote'],
 [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
 ['link', 'clean']
 ],
};

const VendorWorkOrderEdit = () => {
 const navigate = useNavigate();
 const { id } = useParams();
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get("project") || "";
  const workOrderFromUrl = searchParams.get("workOrder") || "";
 const [loading, setLoading] = useState(false);
 const [fetchingData, setFetchingData] = useState(true);
 const [uploadingFile, setUploadingFile] = useState(false);
 const [vendors, setVendors] = useState([]);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [offices, setOffices] = useState([]);
 const [units, setUnits] = useState([]);
 const sigPadRef = useRef(null);
 const [showSignatureModal, setShowSignatureModal] = useState(false);

 // Payment Terms stages (STANDARD mode)
 const PAYMENT_STAGES = ["Advance", "On Delivery", "After Installation", "After Testing", "On Completion", "Retention Release"];

 // Global payment terms state 
 const [paymentTerms, setPaymentTerms] = useState([{ name: "", percentage: "" }]);
 const [paymentMode, setPaymentMode] = useState("CUSTOM");
 const [showGlobalTermsModal, setShowGlobalTermsModal] = useState(false);
 const paymentTotal = paymentTerms.reduce((sum, t) => sum + Number(t.percentage || 0), 0);

 // Per-item payment terms modal state 
 const [showItemTermsModal, setShowItemTermsModal] = useState(false);
 const [itemTermsIndex, setItemTermsIndex] = useState(null);
 const [itemTerms, setItemTerms] = useState([{ name: "", percentage: "" }]);
 const [itemTermsMode, setItemTermsMode] = useState("CUSTOM");
 const itemTermsTotal = itemTerms.reduce((sum, t) => sum + Number(t.percentage || 0), 0);

 const openItemTerms = (index) => {
 const item = form.items[index];
 if (item.paymentTerms?.length) {
 setItemTermsMode(item.paymentMode || "CUSTOM");
 setItemTerms(item.paymentTerms.map((t) => ({ name: t.name || t.stage || "", percentage: t.percentage || "" })));
 } else {
 // pre-fill from global if set
 if (paymentTerms.filter(t => t.name?.trim()).length > 0) {
 setItemTermsMode(paymentMode);
 setItemTerms(paymentTerms.map((t) => ({ ...t })));
 } else {
 setItemTermsMode("CUSTOM");
 setItemTerms([{ name: "", percentage: "" }]);
 }
 }
 setItemTermsIndex(index);
 setShowItemTermsModal(true);
 };

 const saveItemTerms = () => {
 if (itemTermsTotal !== 100) {
 swal("Invalid", "Total must be 100%", "warning");
 return;
 }
 const cleaned = itemTerms.filter((t) => t.name?.trim() || t.percentage).map((t) => ({
 name: itemTermsMode === "CUSTOM" ? (t.name || "").trim() : "",
 stage: itemTermsMode === "STANDARD" ? (t.name || "").trim() : "",
 percentage: Number(t.percentage || 0),
 }));
 const updatedItems = [...form.items];
 updatedItems[itemTermsIndex] = { ...updatedItems[itemTermsIndex], paymentMode: itemTermsMode, paymentTerms: cleaned };
 setForm((prev) => ({ ...prev, items: updatedItems }));
 setShowItemTermsModal(false);
 };

 const clearItemTerms = (index) => {
 const updatedItems = [...form.items];
 updatedItems[index] = { ...updatedItems[index], paymentMode: "CUSTOM", paymentTerms: [] };
 setForm((prev) => ({ ...prev, items: updatedItems }));
 };

 const [form, setForm] = useState({
 vendor: "",
 project: "",
 workOrder: "",
 title: "",
 description: "",
 woNumber: "",
 items: [
 {
 itemName: "",
 description: "",
 quantity: "",
 unit: "",
 rate: "",
 amount: 0,
 },
 ],
 manualBillTo: {
 name: "",
 address: "",
 phone: "",
 gstNumber: "",
 state: "",
 stateCode: "",
 },
 manualShipTo: {
 name: "",
 address: "",
 phone: "",
 state: "",
 stateCode: "",
 projectName: "",
 },
 subTotal: 0,
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 totalAmount: 0,
 termsAndConditions: "",
 status: "Pending",
 issueDate: "",
 expectedCompletionDate: "",
 workOrderDocument: null,
 quotationDocument: null,
 preparedBySign: null,
 remarks: "",
 });

  const statuses = [
    "Draft",
    "Pending",
    "Issued",
    "In Progress",
    "Completed",
    "Closed",
  ];

  useEffect(() => {
    fetchVendors();
    fetchProjects();
    fetchWorkOrders();
    fetchOffices();
    fetchUnits();
    fetchVendorWorkOrder();
  }, [id]);

 const fetchVendors = async () => {
 try {
 const response = await api.get("/vendors?limit=200");
 if (response.data.success) setVendors(response.data.data);
 } catch (error) {
 console.error("Error fetching vendors:", error);
 }
 };

 const fetchUnits = async () => {
 try {
 const res = await api.get("/boq-unit-types");
 if (res.data.success) setUnits(res.data.data);
 } catch (err) {
 console.error("Failed to fetch units", err);
 }
 };

 const fetchProjects = async () => {
 try {
 const response = await api.get("/projects?limit=1000");
 if (response.data.success) setProjects(response.data.data || []);
 } catch (error) {
 console.error("Error fetching projects:", error);
 }
 };

 const fetchWorkOrders = async () => {
 try {
 const response = await api.get("/work-orders?limit=1000");
 if (response.data.success) setWorkOrders(response.data.data || []);
 } catch (error) {
 console.error("Error fetching work orders:", error);
 }
 };

 const selectedProjectWorkOrders = useMemo(() => {
 const activeProj = form.project || projectFromUrl;
 if (!activeProj) return workOrders;
 return workOrders.filter((wo) => String(wo.project?._id || wo.project || "") === String(activeProj));
 }, [workOrders, form.project, projectFromUrl]);

 const getWorkOrderProjectId = (workOrderId) => {
 const workOrder = workOrders.find((wo) => String(wo._id) === String(workOrderId));
 return workOrder?.project?._id || workOrder?.project || "";
 };

 const fetchVendorWorkOrder = async () => {
 try {
 setFetchingData(true);
 const response = await api.get(`/vendor-work-orders/${id}`);
 if (response.data.success) {
 const data = response.data.data;
 setForm({
 vendor:
 typeof data.vendor === "object" ? data.vendor._id : data.vendor || "",
 project:
 typeof data.project === "object" ? data.project._id : data.project || "",
 workOrder:
 typeof data.workOrder === "object" ? data.workOrder._id : data.workOrder || "",
 title: data.title || "",
 woNumber: data.woNumber || "",
 description: data.description || "",
 items: data.items?.length
 ? data.items.map((item) => ({
 itemName: item.itemName || "",
 description: item.description || "",
 quantity: item.quantity || "",
 unit: item.unit || "",
 rate: item.rate || "",
 amount: item.amount || 0,
 paymentMode: item.paymentMode || "CUSTOM",
 paymentTerms: item.paymentTerms || [],
 }))
 : [{ itemName: "", description: "", quantity: "", unit: "", rate: "", amount: 0, paymentMode: "CUSTOM", paymentTerms: [] }],
 manualBillTo: data.manualBillTo || { name: "", address: "", phone: "", gstNumber: "", state: "", stateCode: "" },
 manualShipTo: data.manualShipTo || { name: "", address: "", phone: "", state: "", stateCode: "", projectName: "" },
 subTotal: data.subTotal || 0,
 cgst: data.cgst || 0,
 sgst: data.sgst || 0,
 igst: data.igst || 0,
 cgstAmount: data.cgstAmount || 0,
 sgstAmount: data.sgstAmount || 0,
 igstAmount: data.igstAmount || 0,
 totalAmount: data.totalAmount || 0,
 termsAndConditions: data.termsAndConditions || "",
 status: data.status || "Pending",
 issueDate: data.issueDate ? new Date(data.issueDate).toISOString().split("T")[0] : "",
 expectedCompletionDate: data.expectedCompletionDate
 ? new Date(data.expectedCompletionDate).toISOString().split("T")[0]
 : "",
 workOrderDocument: data.workOrderDocument || null,
 quotationDocument: data.quotationDocument || null,
 preparedBySign: data.preparedBySign || null,
 remarks: data.remarks || "",
 });

 // Load payment terms
 if (data.paymentTerms?.length) {
 setPaymentMode(data.paymentMode || "CUSTOM");
 setPaymentTerms(
 data.paymentTerms.map((t) => ({
 name: t.name || t.stage || "",
 percentage: t.percentage || "",
 }))
 );
 } }
 } catch (error) {
 swal("Error", "Failed to load vendor work order", "error");
 navigate("/vendor-work-orders");
 } finally {
 setFetchingData(false);
 }
 };

  const fetchOffices = async () => {
    try {
      const res = await api.get("/offices");
      if (res.data.success && Array.isArray(res.data.data)) {
        setOffices(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching offices:", err);
    }
  };

  const getProjectOfficeState = (projectId) => {
    if (!projectId) return "";
    const project = projects.find((p) => String(p._id) === String(projectId));
    const office = project?.office;
    let st = "";
    if (office && typeof office === "object") {
      st = office.state || "";
      if (!st && office.address) {
        const addr = office.address.toLowerCase();
        if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
        else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
      }
    } else if (office) {
      const officeId = office._id || office;
      const found = offices.find((o) => String(o._id) === String(officeId));
      if (found) {
        st = found.state || "";
        if (!st && found.address) {
          const addr = found.address.toLowerCase();
          if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
          else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
        }
      }
    }
    return st;
  };

  const getVendorDetails = (vendorId) => {
    if (!vendorId) return { state: "", hasGst: false };
    const vendor = vendors.find((v) => String(v._id) === String(vendorId));
    return {
      state: vendor?.state || "",
      hasGst: Boolean(vendor?.gst && String(vendor.gst).trim()),
    };
  };

  const applyGST = (vendorState, officeState, hasGst) => {
    if (!hasGst) {
      return { cgst: 0, sgst: 0, igst: 0 };
    }
    const normalize = (s) => (s || "").toString().trim().toLowerCase();
    const vState = normalize(vendorState);
    const oState = normalize(officeState);

    if (vState && oState && vState === oState) {
      return { cgst: 9, sgst: 9, igst: 0 };
    }
    return { cgst: 0, sgst: 0, igst: 18 };
  };

  // Reactive GST calculation whenever vendor, project or workOrder changes
  useEffect(() => {
    if (!form.vendor) return;
    const { state: vendorState, hasGst } = getVendorDetails(form.vendor);
    const effectiveProjectId = form.project || getWorkOrderProjectId(form.workOrder);
    const officeState = getProjectOfficeState(effectiveProjectId);

    const { cgst, sgst, igst } = applyGST(vendorState, officeState, hasGst);
    setForm((prev) => {
      if (prev.cgst === cgst && prev.sgst === sgst && prev.igst === igst) {
        return prev;
      }
      return { ...prev, cgst, sgst, igst };
    });
  }, [form.vendor, form.project, form.workOrder, vendors, projects, offices]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "vendor") {
      setForm((prev) => ({ ...prev, vendor: value }));
      return;
    }

 if (name === "project") {
 setForm((prev) => ({
 ...prev,
 project: value,
 workOrder: prev.workOrder && String(getWorkOrderProjectId(prev.workOrder)) === String(value)
 ? prev.workOrder
 : "",
 }));
 return;
 }

 if (name === "workOrder") {
 const workOrderProjectId = getWorkOrderProjectId(value);
 setForm((prev) => ({
 ...prev,
 workOrder: value,
 project: workOrderProjectId || prev.project || "",
 }));
 return;
 }

 setForm((prev) => ({ ...prev, [name]: value }));
 };

 const handleManualChange = (section, field, value) => {
 setForm((prev) => ({
 ...prev,
 [section]: { ...prev[section], [field]: value },
 }));
 };

 const handleItemChange = (index, field, value) => {
 const updatedItems = [...form.items];
 updatedItems[index][field] = value;
 if (field === "quantity" || field === "rate") {
 const quantity = parseFloat(updatedItems[index].quantity) || 0;
 const rate = parseFloat(updatedItems[index].rate) || 0;
 updatedItems[index].amount = quantity * rate;
 }
 setForm((prev) => ({ ...prev, items: updatedItems }));
 };

 const addItem = () => {
 setForm((prev) => ({
 ...prev,
 items: [
 ...prev.items,
 { itemName: "", description: "", quantity: "", unit: "", rate: "", amount: 0 },
 ],
 }));
 };

 const removeItem = async (index) => {
 if (form.items.length === 1) {
 swal("Warning", "At least one item is required", "warning");
 return;
 }
 const confirmDelete = await swal({
 title: "Are you sure?",
 text: "This item will be removed!",
 icon: "warning",
 buttons: ["Cancel", "Remove"],
 dangerMode: true,
 });
 if (!confirmDelete) return;
 const updatedItems = form.items.filter((_, i) => i !== index);
 setForm((prev) => ({ ...prev, items: updatedItems }));
 };

 const calculateTotal = (items) => {
 const subTotal = items.reduce(
 (sum, item) =>
 sum + parseFloat(item.quantity || 0) * parseFloat(item.rate || 0),
 0
 );
 const cgstAmount = (subTotal * form.cgst) / 100;
 const sgstAmount = (subTotal * form.sgst) / 100;
 const igstAmount = (subTotal * form.igst) / 100;
 const total = subTotal + cgstAmount + sgstAmount + igstAmount;
 setForm((prev) => ({
 ...prev,
 subTotal,
 cgstAmount,
 sgstAmount,
 igstAmount,
 totalAmount: total,
 }));
 };

 useEffect(() => {
 if (!fetchingData) {
 calculateTotal(form.items);
 }
 }, [form.items, form.cgst, form.sgst, form.igst]);

 const handleFileUpload = async (e, fieldName) => {
 const file = e.target.files[0];
 if (!file) return;
 try {
 setUploadingFile(true);
 const formData = new FormData();
 formData.append("file", file);
 const response = await api.post("/vendor-work-orders/upload/single", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 if (response.data.success) {
 setForm((prev) => ({
 ...prev,
 [fieldName]: {
 fileName: response.data.fileName,
 fileUrl: response.data.fileUrl,
 filePath: response.data.filePath,
 },
 }));
 swal("Uploaded!", `${file.name} uploaded successfully!`, "success");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to upload file", "error");
 } finally {
 setUploadingFile(false);
 }
 };

 const dataURLtoBlob = (dataURL) => {
 const arr = dataURL.split(",");
 const mime = arr[0].match(/:(.*?);/)[1];
 const bstr = atob(arr[1]);
 let n = bstr.length;
 const u8arr = new Uint8Array(n);
 while (n--) u8arr[n] = bstr.charCodeAt(n);
 return new Blob([u8arr], { type: mime });
 };

 const clearSignature = () => {
 sigPadRef.current?.clear();
 };

 const saveSignature = async () => {
 if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
 swal("Error", "Please sign before saving", "error");
 return;
 }

 try {
 setUploadingFile(true);
 const dataURL = sigPadRef.current.toDataURL("image/png");
 const signature = {
 fileName: "vendor-work-order-signature.png",
 sign: dataURL,
 uploadedAt: new Date().toISOString(),
 date: new Date().toISOString(),
 };
 setForm((prev) => ({ ...prev, preparedBySign: signature }));
 setShowSignatureModal(false);
 swal("Success", "Signature captured", "success");
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to capture signature", "error");
 } finally {
 setUploadingFile(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.vendor) {
 swal("Missing Input", "Please select a vendor", "warning");
 return;
 }
 if (!form.title.trim()) {
 swal("Missing Input", "Please enter a title", "warning");
 return;
 }
 if (form.items.some((item) => !item.itemName || !item.quantity || !item.rate)) {
 swal("Missing Item Details", "Please fill all item fields", "warning");
 return;
 }

 // Payment terms validation — check each item individually
 const itemsWithInvalidTerms = form.items.filter((item) => {
 if (!item.paymentTerms?.length) return false;
 const total = item.paymentTerms.reduce((sum, t) => sum + Number(t.percentage || 0), 0);
 return total !== 100;
 });
 if (itemsWithInvalidTerms.length > 0) {
 swal("Invalid Payment Terms", `Item "${itemsWithInvalidTerms[0].itemName}" payment terms must total 100%`, "warning");
 return;
 }
 // global terms validation (only if filled)
 const filledGlobal = paymentTerms.filter((t) => t.name?.trim() || t.percentage);
 if (filledGlobal.length > 0 && paymentTotal !== 100) {
 swal("Invalid Global Payment Terms", "Global payment terms total must be exactly 100%", "warning");
 return;
 }

 try {
 setLoading(true);
 const cleanedGlobal = filledGlobal.map((t) => ({
 name: paymentMode === "CUSTOM" ? (t.name || "").trim() : "",
 stage: paymentMode === "STANDARD" ? (t.name || "").trim() : "",
 percentage: Number(t.percentage || 0),
 }));
 const payload = {
 ...form,
 project: form.project || null,
 workOrder: form.workOrder || null,
 paymentMode,
 paymentTerms: cleanedGlobal,
 items: form.items.map((item) => ({
 ...item,
 paymentTerms: item.paymentTerms || [],
 paymentMode: item.paymentMode || "CUSTOM",
 })),
 };
 const response = await api.patch(`/vendor-work-orders/${id}`, payload);
 if (response.data.success) {
 swal("Success!", "Vendor Work Order updated successfully!", "success");
 navigate("/vendor-work-orders");
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to update Vendor Work Order",
 "error"
 );
 } finally {
 setLoading(false);
 }
 };

 if (fetchingData) {
 return (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading vendor work order...</p>
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Edit Vendor Work Order" motherMenu="Commercial" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Edit Vendor Work Order</Card.Title>
 <Button variant="secondary" onClick={() => navigate("/vendor-work-orders")}>
 <i className="fas fa-arrow-left me-2"></i>Back
 </Button>
 </Card.Header>

 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {/* Basic Details */}
 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>WO Number</Form.Label>
 <Form.Control
 type="text"
 value={form.woNumber}
 readOnly
 disabled
 className="bg-light fw-bold"
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>
 Vendor <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="vendor"
 value={form.vendor}
 onChange={handleChange}
 required
 >
 <option value="">Select Vendor</option>
 {vendors.map((vendor) => (
 <option key={vendor._id} value={vendor._id}>
 {vendor.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Title / Scope <span className="text-danger">*</span></Form.Label>
 <Form.Control
 type="text"
 name="title"
 value={form.title}
 onChange={handleChange}
 placeholder="Enter work order title or scope of work"
 required
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={12}>
 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="description"
 value={form.description}
 onChange={handleChange}
 placeholder="Brief description of the work"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row className="g-3">
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Project <span className="text-muted">(Optional)</span></Form.Label>
 <Form.Select
 name="project"
 value={form.project}
 onChange={handleChange}
 >
 <option value="">Select Project</option>
 {projects.map((project) => (
 <option key={project._id} value={project._id}>
 {project.projectCode ? `${project.projectCode} - ` : ""}
 {project.projectName || project.name || "Project"}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Work Order <span className="text-muted">(Optional)</span></Form.Label>
 <Form.Select
 name="workOrder"
 value={form.workOrder}
 onChange={handleChange}
 >
 <option value="">Select Work Order</option>
 {selectedProjectWorkOrders.map((workOrder) => (
 <option key={workOrder._id} value={workOrder._id}>
 {workOrder.woNo || workOrder.woNumber || "WO"}
 {workOrder.label ? ` – ${workOrder.label}` : ""}
 {workOrder.project?.projectName || workOrder.project?.name
 ? ` - ${workOrder.project.projectName || workOrder.project.name}`
 : ""}
 </option>
 ))}
 </Form.Select>
 <Form.Text className="text-muted">Optional, only for reference.</Form.Text>
 </Form.Group>
 </Col>
 </Row>

 {/* Manual Bill To / Ship To */}
 <div className="mb-4 border p-3 rounded">
 <Row>
 <Col md={6} className="border-end">
 <h6 className="text-primary mb-3">MANUAL BILL TO</h6>
 <Form.Group className="mb-2">
 <Form.Label>Client Name</Form.Label>
 <Form.Control
 type="text"
 value={form.manualBillTo.name}
 onChange={(e) => handleManualChange("manualBillTo", "name", e.target.value)}
 />
 </Form.Group>
 <Form.Group className="mb-2">
 <Form.Label>Address</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={form.manualBillTo.address}
 onChange={(e) => handleManualChange("manualBillTo", "address", e.target.value)}
 />
 </Form.Group>
 <Row>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>GSTIN</Form.Label>
 <Form.Control
 type="text"
 value={form.manualBillTo.gstNumber}
 onChange={(e) => handleManualChange("manualBillTo", "gstNumber", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>Phone</Form.Label>
 <Form.Control
 type="text"
 value={form.manualBillTo.phone}
 onChange={(e) => handleManualChange("manualBillTo", "phone", e.target.value)}
 />
 </Form.Group>
 </Col>
 </Row>
 <Row>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>State</Form.Label>
 <Form.Control
 type="text"
 value={form.manualBillTo.state}
 onChange={(e) => handleManualChange("manualBillTo", "state", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>State Code</Form.Label>
 <Form.Control
 type="text"
 value={form.manualBillTo.stateCode}
 onChange={(e) => handleManualChange("manualBillTo", "stateCode", e.target.value)}
 />
 </Form.Group>
 </Col>
 </Row>
 </Col>

 <Col md={6}>
 <h6 className="text-primary mb-3">MANUAL SHIP TO</h6>
 <Form.Group className="mb-2">
 <Form.Label>Name</Form.Label>
 <Form.Control
 type="text"
 value={form.manualShipTo.name}
 onChange={(e) => handleManualChange("manualShipTo", "name", e.target.value)}
 />
 </Form.Group>
 <Form.Group className="mb-2">
 <Form.Label>Shipping Address</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={form.manualShipTo.address}
 onChange={(e) => handleManualChange("manualShipTo", "address", e.target.value)}
 />
 </Form.Group>
 <Form.Group className="mb-2">
 <Form.Label>Project Name (Internal)</Form.Label>
 <Form.Control
 type="text"
 value={form.manualShipTo.projectName}
 onChange={(e) => handleManualChange("manualShipTo", "projectName", e.target.value)}
 />
 </Form.Group>
 <Row>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>State</Form.Label>
 <Form.Control
 type="text"
 value={form.manualShipTo.state}
 onChange={(e) => handleManualChange("manualShipTo", "state", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col sm={6}>
 <Form.Group className="mb-2">
 <Form.Label>State Code</Form.Label>
 <Form.Control
 type="text"
 value={form.manualShipTo.stateCode}
 onChange={(e) => handleManualChange("manualShipTo", "stateCode", e.target.value)}
 />
 </Form.Group>
 </Col>
 </Row>
 </Col>
 </Row>
 </div>

 {/* Status & Dates */}
 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange}>
 {statuses.map((status, i) => (
 <option key={i} value={status}>{status}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Issue Date</Form.Label>
 <Form.Control
 type="date"
 name="issueDate"
 value={form.issueDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Expected Completion Date</Form.Label>
 <Form.Control
 type="date"
 name="expectedCompletionDate"
 value={form.expectedCompletionDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 </Row>

 {/* Items Table */}
 <div className="mb-3">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <h6 className="mb-0">Scope of Work / Items</h6>
 <div className="d-flex gap-2">
 <Button
 variant={paymentTotal === 100 ? "warning" : "outline-warning"}
 size="sm"
 onClick={() => setShowGlobalTermsModal(true)}
 title="Set global payment terms (applies to all items without individual terms)"
 >
 <i className="fas fa-rupee-sign me-1"></i>
 Payment Terms
 {paymentTotal === 100 && <i className="fas fa-check-circle ms-1 text-success"></i>}
 </Button>
 <Button variant="outline-success" size="sm" onClick={addItem}>
 <i className="fas fa-plus me-1"></i> Add Item
 </Button>
 </div>
 </div>

 <div className="table-responsive">
 <table className="table table-bordered align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: "5%" }}>#</th>
 <th style={{ width: "20%" }}>Item Name</th>
 <th style={{ width: "20%" }}>Description</th>
 <th style={{ width: "9%" }}>Qty</th>
 <th style={{ width: "9%" }}>Unit</th>
 <th style={{ width: "12%" }}>Rate</th>
 <th style={{ width: "12%" }}>Amount</th>
 <th style={{ width: "8%" }}>Terms</th>
 <th style={{ width: "5%" }}></th>
 </tr>
 </thead>
 <tbody>
 {form.items.map((item, index) => (
 <tr key={index}>
 <td className="text-center">{index + 1}</td>
 <td>
 <Form.Control
 type="text"
 value={item.itemName}
 onChange={(e) => handleItemChange(index, "itemName", e.target.value)}
 placeholder="Item name"
 />
 </td>
 <td>
 <Form.Control
 type="text"
 value={item.description}
 onChange={(e) => handleItemChange(index, "description", e.target.value)}
 placeholder="Description"
 />
 </td>
 <td>
 <Form.Control
 type="number"
 min="0"
 value={item.quantity}
 onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
 />
 </td>
 <td>
 <Form.Select
 value={item.unit}
 onChange={(e) => handleItemChange(index, "unit", e.target.value)}
 >
 <option value="">Unit</option>
 {units.map((u) => (
 <option key={u._id} value={u.name}>{u.name}</option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control
 type="number"
 min="0"
 value={item.rate}
 onChange={(e) => handleItemChange(index, "rate", e.target.value)}
 />
 </td>
 <td>
 <Form.Control
 type="number"
 value={Number(item.amount).toFixed(2)}
 readOnly
 disabled
 className="bg-light"
 />
 </td>
 <td className="text-center">
 <Button
 variant={item.paymentTerms?.length ? "warning" : "outline-secondary"}
 size="sm"
 title={item.paymentTerms?.length ? "Item-specific terms set" : "Use global terms"}
 onClick={() => openItemTerms(index)}
 >
 <i className="fas fa-rupee-sign"></i>
 </Button>
 {item.paymentTerms?.length > 0 && (
 <Button
 variant="link"
 size="sm"
 className="text-danger p-0 ms-1"
 title="Clear item terms"
 onClick={() => clearItemTerms(index)}
 >
 <i className="fas fa-times"></i>
 </Button>
 )}
 </td>
 <td className="text-center">
 <Button
 variant="outline-danger"
 size="sm"
 onClick={() => removeItem(index)}
 >
 <i className="fas fa-trash"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>

 {/* Financials Summary */}
 <Row className="justify-content-end mb-3">
 <Col md={5}>
 <div className="border rounded p-3">
 <div className="d-flex justify-content-between mb-1">
 <span>Sub Total:</span>
 <strong>Rs. {Number(form.subTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>CGST ({form.cgst}%):</span>
 <span>Rs. {Number(form.cgstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>SGST ({form.sgst}%):</span>
 <span>Rs. {Number(form.sgstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>IGST ({form.igst}%):</span>
 <span>Rs. {Number(form.igstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
 </div>
 <hr className="my-2" />
 <div className="d-flex justify-content-between">
 <strong>Grand Total:</strong>
 <strong className="text-success fs-6">
 Rs. {Number(form.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
 </strong>
 </div>
 </div>
 </Col>
 </Row>

 {/* Terms & Remarks */}
 <Form.Group className="mb-5">
 <Form.Label>Terms &amp; Conditions</Form.Label>
 <div style={{ height: "auto", marginBottom: "20px" }}>
 <ReactQuill
 theme="snow"
 value={form.termsAndConditions}
 onChange={(val) => setForm((prev) => ({ ...prev, termsAndConditions: val }))}
 modules={quillModules}
 placeholder="Enter terms and conditions..."
 style={{ height: "auto" }}
 />
 </div>
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 placeholder="Internal remarks..."
 />
 </Form.Group>

 <Row className="mb-3">
 <Col md={12}>
 <div className="border rounded p-3">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <Form.Label className="mb-0">Prepared By Signature</Form.Label>
 <Button variant="outline-primary" size="sm" onClick={() => setShowSignatureModal(true)}>
 {form.preparedBySign ? "Re-sign" : "Add Signature"}
 </Button>
 </div>
 {form.preparedBySign ? (
 <div className="d-flex align-items-center gap-3">
 <img
 src={form.preparedBySign.sign || form.preparedBySign.fileUrl}
 alt="Prepared By Signature"
 style={{ maxHeight: 80, maxWidth: 260, objectFit: "contain", border: "1px solid #ddd", background: "#fff" }}
 />
 <div>
 <div className="text-success fw-semibold">{form.preparedBySign.fileName}</div>
 <small className="text-muted">Signature captured in form</small>
 </div>
 </div>
 ) : (
 <small className="text-muted">No signature captured yet.</small>
 )}
 </div>
 </Col>
 </Row>

 {/* Documents */}
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Work Order Document</Form.Label>
 <Form.Control
 type="file"
 accept=".jpg,.jpeg,.png,.gif,.pdf"
 onChange={(e) => handleFileUpload(e, "workOrderDocument")}
 disabled={uploadingFile}
 />
 {form.workOrderDocument && (
 <Form.Text className="text-success">
 Uploaded: {form.workOrderDocument.fileName}
 </Form.Text>
 )}
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Quotation Document</Form.Label>
 <Form.Control
 type="file"
 accept=".jpg,.jpeg,.png,.gif,.pdf"
 onChange={(e) => handleFileUpload(e, "quotationDocument")}
 disabled={uploadingFile}
 />
 {form.quotationDocument && (
 <Form.Text className="text-success">
 Uploaded: {form.quotationDocument.fileName}
 </Form.Text>
 )}
 </Form.Group>
 </Col>
 </Row>

 <div className="d-flex justify-content-end gap-2 mt-3">
 <Button variant="secondary" onClick={() => navigate("/vendor-work-orders")} type="button">
 Cancel
 </Button>
 <Button variant="primary" type="submit" disabled={loading || uploadingFile}>
 {loading ? (
 <>
 <span className="spinner-border spinner-border-sm me-2"></span>
 {uploadingFile ? "Uploading..." : "Saving..."}
 </>
 ) : (
 "Save Changes"
 )}
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Global Payment Terms Modal */}
 <Modal show={showGlobalTermsModal} onHide={() => setShowGlobalTermsModal(false)} centered size="lg">
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title className="fw-bold fs-6">
 <i className="fas fa-rupee-sign text-warning me-2"></i>
 Global Payment Terms
 <small className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>
 Applies to all items that don't have individual terms
 </small>
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-4">
 <div className="mb-3 d-flex align-items-center gap-2">
 <Form.Label className="mb-0 fw-semibold">Mode:</Form.Label>
 <Form.Select size="sm" style={{ width: 180 }} value={paymentMode}
 onChange={(e) => { setPaymentMode(e.target.value); setPaymentTerms([{ name: "", percentage: "" }]); }}>
 <option value="CUSTOM">Custom Milestones</option>
 <option value="STANDARD">Standard Stages</option>
 </Form.Select>
 </div>
 <table className="table table-bordered align-middle mb-2">
 <thead className="table-light">
 <tr>
 <th style={{ width: "5%" }}>#</th>
 <th>Stage / Milestone</th>
 <th style={{ width: "160px" }} className="text-center">%</th>
 <th style={{ width: "40px" }}></th>
 </tr>
 </thead>
 <tbody>
 {paymentTerms.map((row, i) => (
 <tr key={i}>
 <td className="text-center text-muted">{i + 1}</td>
 <td>
 {paymentMode === "STANDARD" ? (
 <Form.Select size="sm" value={row.name} className="border-0 shadow-none"
 onChange={(e) => setPaymentTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))}>
 <option value="">Select Stage</option>
 {PAYMENT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
 </Form.Select>
 ) : (
 <Form.Control size="sm" type="text" placeholder="e.g. 30% Advance on PO..." className="border-0 shadow-none"
 value={row.name}
 onChange={(e) => setPaymentTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} />
 )}
 </td>
 <td>
 <Form.Control size="sm" type="number" min="0" max="100" className="border-0 shadow-none text-center fw-bold"
 value={row.percentage}
 onChange={(e) => setPaymentTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, percentage: e.target.value } : r))} />
 </td>
 <td className="text-center">
 <Button variant="link" size="sm" className="text-danger p-0"
 onClick={() => setPaymentTerms((prev) => prev.filter((_, idx) => idx !== i))}
 disabled={paymentTerms.length === 1}>
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 <div className="d-flex justify-content-between align-items-center">
 <Button variant="link" className="p-0 text-primary text-decoration-none fw-bold"
 onClick={() => setPaymentTerms((prev) => [...prev, { name: "", percentage: "" }])}>
 + Add Row
 </Button>
 <span className={`fw-bold ${paymentTotal === 100 ? "text-success" : paymentTotal > 0 ? "text-danger" : "text-muted"}`}>
 Total: {paymentTotal}% {paymentTotal === 100 && <i className="fas fa-check-circle"></i>}
 </span>
 </div>
 </Modal.Body>
 <Modal.Footer className="bg-light">
 <Button variant="secondary" onClick={() => setShowGlobalTermsModal(false)}>Cancel</Button>
 <Button variant="primary" disabled={paymentTotal !== 100}
 onClick={() => { setShowGlobalTermsModal(false); }}>
 Save Global Terms
 </Button>
 </Modal.Footer>
 </Modal>

 {/* Per-Item Payment Terms Modal */}
 <Modal show={showItemTermsModal} onHide={() => setShowItemTermsModal(false)} centered size="lg">
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title className="fw-bold fs-6">
 <i className="fas fa-rupee-sign text-warning me-2"></i>
 Item Payment Terms — {itemTermsIndex !== null ? form.items[itemTermsIndex]?.itemName || `Item ${itemTermsIndex + 1}` : ""}
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-4">
 <div className="mb-3 d-flex align-items-center gap-2">
 <Form.Label className="mb-0 fw-semibold">Mode:</Form.Label>
 <Form.Select size="sm" style={{ width: 180 }} value={itemTermsMode}
 onChange={(e) => { setItemTermsMode(e.target.value); setItemTerms([{ name: "", percentage: "" }]); }}>
 <option value="CUSTOM">Custom Milestones</option>
 <option value="STANDARD">Standard Stages</option>
 </Form.Select>
 </div>
 <table className="table table-bordered align-middle mb-2">
 <thead className="table-light">
 <tr>
 <th style={{ width: "5%" }}>#</th>
 <th>Stage / Milestone</th>
 <th style={{ width: "160px" }} className="text-center">%</th>
 <th style={{ width: "40px" }}></th>
 </tr>
 </thead>
 <tbody>
 {itemTerms.map((row, i) => (
 <tr key={i}>
 <td className="text-center text-muted">{i + 1}</td>
 <td>
 {itemTermsMode === "STANDARD" ? (
 <Form.Select size="sm" value={row.name} className="border-0 shadow-none"
 onChange={(e) => setItemTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))}>
 <option value="">Select Stage</option>
 {PAYMENT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
 </Form.Select>
 ) : (
 <Form.Control size="sm" type="text" placeholder="e.g. 30% Advance..." className="border-0 shadow-none"
 value={row.name}
 onChange={(e) => setItemTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} />
 )}
 </td>
 <td>
 <Form.Control size="sm" type="number" min="0" max="100" className="border-0 shadow-none text-center fw-bold"
 value={row.percentage}
 onChange={(e) => setItemTerms((prev) => prev.map((r, idx) => idx === i ? { ...r, percentage: e.target.value } : r))} />
 </td>
 <td className="text-center">
 <Button variant="link" size="sm" className="text-danger p-0"
 onClick={() => setItemTerms((prev) => prev.filter((_, idx) => idx !== i))}
 disabled={itemTerms.length === 1}>
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 <div className="d-flex justify-content-between align-items-center">
 <Button variant="link" className="p-0 text-primary text-decoration-none fw-bold"
 onClick={() => setItemTerms((prev) => [...prev, { name: "", percentage: "" }])}>
 + Add Row
 </Button>
 <span className={`fw-bold ${itemTermsTotal === 100 ? "text-success" : itemTermsTotal > 0 ? "text-danger" : "text-muted"}`}>
 Total: {itemTermsTotal}% {itemTermsTotal === 100 && <i className="fas fa-check-circle"></i>}
 </span>
 </div>
 </Modal.Body>
 <Modal.Footer className="bg-light">
 <Button variant="secondary" onClick={() => setShowItemTermsModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={saveItemTerms} disabled={itemTermsTotal !== 100}>Save Item Terms</Button>
 </Modal.Footer>
 </Modal>

 <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Capture Prepared By Signature</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="border rounded bg-white" style={{ touchAction: "none" }}>
 <SignatureCanvas
 ref={sigPadRef}
 penColor="black"
 canvasProps={{
 width: 800,
 height: 260,
 className: "w-100",
 style: { width: "100%", height: 260, background: "#fff" },
 }}
 />
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="outline-secondary" onClick={clearSignature} type="button">
 Clear
 </Button>
 <Button variant="secondary" onClick={() => setShowSignatureModal(false)} type="button">
 Cancel
 </Button>
 <Button variant="primary" onClick={saveSignature} disabled={uploadingFile} type="button">
 Save Signature
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default VendorWorkOrderEdit;
